import React, { useState, useEffect, useCallback } from 'react'
import { Button, Card } from 'react-daisyui'
import { API } from '../../utils/api'
import { Api } from '../../utils/apiServer'
import moment from 'moment'
import { AiOutlineCalendar, AiOutlineTrophy, AiOutlineGift, AiOutlineShoppingCart, AiOutlineHistory } from "react-icons/ai"
import { FaCoins } from "react-icons/fa"

function History() {
    const [pointHistory, setPointHistory] = useState([])
    const [page, setPage] = useState(1)
    const [lastPage, setLastPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [totalPoints, setTotalPoints] = useState(0)


    const loadPointHistory = useCallback(async (targetPage = 1, append = false) => {
        if (append) setLoadingMore(true)
        else setLoading(true)
        const params = {
            access: 'auth',
            method: API.POINT.type,
            url: API.POINT.url,
            reqBody: {
                page: targetPage,
                rows: 10
            }
        }
        try {
            const response = await Api(params)
            if (response?.status) {
                setPointHistory((currentHistory) => {
                    const nextHistory = append ? [...currentHistory, ...response.data.data] : response.data.data
                    setTotalPoints(nextHistory.reduce((sum, item) => sum + parseInt(item.point || 0, 10), 0))
                    return nextHistory
                })
                setLastPage(response.data.lastPage)
                setPage(response.data.page)
            }
        } catch (error) {
            console.error('Error loading point history:', error)
        } finally {
            if (append) setLoadingMore(false)
            else setLoading(false)
        }
    }, [])

    useEffect(() => {
        loadPointHistory(1, false)
    }, [loadPointHistory])

    const loadMore = () => {
        if (!loadingMore && page < lastPage) {
            loadPointHistory(page + 1, true)
        }
    }

    // Determine icon and color based on transaction type
    const getTransactionIcon = (description) => {
        const desc = description?.toLowerCase() || ''

        if (desc.includes('redeem') || desc.includes('tukar')) {
            return <AiOutlineGift className="text-purple-600" />
        } else if (desc.includes('earn') || desc.includes('dapat') || desc.includes('bonus')) {
            return <AiOutlineTrophy className="text-green-600" />
        } else if (desc.includes('purchase') || desc.includes('beli')) {
            return <AiOutlineShoppingCart className="text-blue-600" />
        } else {
            return <AiOutlineHistory className="text-gray-600" />
        }
    }

    const getPointColor = (point) => {
        const points = parseInt(point)
        if (points > 0) {
            return 'text-green-600'
        } else if (points < 0) {
            return 'text-red-600'
        }
        return 'text-gray-600'
    }

    // Loading skeleton component
    const LoadingSkeleton = () => (
        <div className="animate-pulse space-y-4">
            <div className="bg-gray-200 h-8 rounded w-1/3 mx-auto"></div>
            <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="bg-gray-200 h-20 rounded-lg"></div>
                ))}
            </div>
        </div>
    )


    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="mx-auto max-w-sm px-5 py-6">
                    <LoadingSkeleton />
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="mx-auto max-w-sm px-5 py-6">
                <div className='flex flex-col space-y-6 scrollbar-hide'>

                    {/* Header Section */}
                    <div className="text-center space-y-2">
                        <div className="flex items-center justify-center space-x-2">
                            <AiOutlineHistory className="text-2xl text-blue-600" />
                            <h1 className="text-2xl font-bold text-gray-800">Riwayat Poin</h1>
                        </div>
                        <p className="text-gray-600 text-sm">Pantau aktivitas poin Anda</p>
                        {totalPoints !== 0 && (
                            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-3 rounded-lg border border-blue-100">
                                <div className="flex items-center justify-center space-x-2">
                                    <FaCoins className="text-amber-500" />
                                    <span className="text-sm font-medium text-gray-700">
                                        Total Poin: <span className="font-bold text-blue-600">{totalPoints}</span>
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* History List */}
                    {pointHistory.length === 0 ? (
                        <Card className="bg-gradient-to-r from-gray-50 to-white border-2 border-dashed border-gray-300">
                            <Card.Body className="text-center py-12">
                                <AiOutlineHistory className="text-6xl text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 font-medium text-lg">Belum ada riwayat poin</p>
                                <p className="text-gray-400 text-sm mt-1">Aktivitas poin akan muncul di sini</p>
                            </Card.Body>
                        </Card>
                    ) : (
                        <div className="space-y-3">
                            {pointHistory.map((item, i) => (
                                <Card key={i} className="bg-white shadow-lg hover:shadow-xl transition-all duration-300 border-0 overflow-hidden">
                                    <Card.Body className="p-0">
                                        <div className="p-4">
                                            <div className="flex items-start space-x-3">
                                                {/* Transaction Icon */}
                                                <div className="flex-shrink-0 mt-1">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                                                        {getTransactionIcon(item.desc)}
                                                    </div>
                                                </div>

                                                {/* Transaction Details */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="flex items-center space-x-2">
                                                            <AiOutlineCalendar className="text-gray-500 text-sm" />
                                                            <span className="text-sm font-medium text-gray-700">
                                                                {moment(item.created_at).format('DD MMM YYYY, HH:mm')}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center space-x-1">
                                                            <FaCoins className="text-amber-500 text-sm" />
                                                            <span className={`font-bold text-lg ${getPointColor(item.point)}`}>
                                                                {parseInt(item.point) > 0 ? '+' : ''}{item.point}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="bg-gray-50 rounded-lg p-3">
                                                        <p className="text-gray-800 font-medium text-sm leading-relaxed">
                                                            {item.desc}
                                                        </p>
                                                    </div>

                                                    {/* Point Change Indicator */}
                                                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                                                        <div className="text-xs text-gray-500">
                                                            {parseInt(item.point) > 0 ? 'Poin didapat' : parseInt(item.point) < 0 ? 'Poin digunakan' : 'Tidak ada perubahan poin'}
                                                        </div>
                                                        <div className={`text-xs font-semibold px-2 py-1 rounded-full ${
                                                            parseInt(item.point) > 0
                                                                ? 'bg-green-100 text-green-700'
                                                                : parseInt(item.point) < 0
                                                                ? 'bg-red-100 text-red-700'
                                                                : 'bg-gray-100 text-gray-700'
                                                        }`}>
                                                            {parseInt(item.point) > 0 ? 'Earned' : parseInt(item.point) < 0 ? 'Spent' : 'Neutral'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}

                    {pointHistory.length > 0 && page < lastPage && (
                        <div className="pt-4 text-center">
                            <Button
                                size="sm"
                                className="rounded-full border border-slate-200 bg-white px-5 text-white"
                                loading={loadingMore}
                                disabled={loadingMore}
                                onClick={loadMore}
                            >
                                Muat lebih banyak
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default History
